import { Sparkles } from 'lucide-react'

import { useAutoplay } from '@/audio/autoplay'
import { usePlayer } from '@/audio/store'
import { useState } from 'react'
import { useHomePlayback } from '@/features/home/useHomePlayback'
import { SearchResultRow } from '@/features/search/SearchResultRow'
import { AddToPlaylistDialog, type AddToPlaylistTarget } from '@/features/search/AddToPlaylistDialog'
import { Button, Spinner, Switch } from '@/components/ui'
import { SwipeSurface } from './SwipeSurface'
import { dismissSuggestion, enqueueWithUndo, queueSongKey, useQueueFeedback } from './queueGestureActions'

export function SimilarQueue() {
  const { enabled, suggestions, loading, message, seedName, setEnabled, play, retry } = useAutoplay()
  const queueIdentity = usePlayer((state) => `${state.session}:${state.index}:${state.queue.map((item) => item.uid).join(',')}`)
  const pending = useQueueFeedback((state) => state.pending)
  const playback = useHomePlayback('queue')
  const [target, setTarget] = useState<AddToPlaylistTarget | null>(null)
  const repeat = usePlayer((state) => state.repeat)
  const waiting = usePlayer((state) => state.awaitingContinuation)

  return (
    <section aria-label="Similar songs" className="mt-5 border-t border-border pt-4">
      <div className="flex items-center justify-between gap-3 px-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
          <Sparkles className="h-4 w-4 text-accent" aria-hidden="true" />
          Similar songs
        </h3>
        <Switch compact label="Autoplay" aria-label="Autoplay similar songs" checked={enabled} onCheckedChange={setEnabled} />
      </div>
      <p aria-live="polite" className="px-2 pb-3 text-xs text-text-muted">
        {!enabled ? 'Stops when your queue ends.' : waiting ? 'Getting the next song ready…' : repeat !== 'off'
          ? 'Autoplay resumes when repeat is off.'
          : seedName ? `After your queue · Inspired by ${seedName}` : 'Plays automatically after your queue.'}
      </p>
      {enabled ? (
        <>
          <ul className="flex flex-col gap-1">
            {suggestions.map(({ key, track, preparing }) => {
              const error = playback.errors[`${track.provider}:${track.external_id}`]
              const adding = pending.includes(queueSongKey(track))
              return <SearchResultRow key={key} result={track} providerName={track.provider}
                wrapRow={(row) => <SwipeSurface identity={`${queueIdentity}:${suggestions.map((item) => item.key).join(',')}`} left="Dismiss" right={adding ? 'Preparing…' : 'Add to queue'} onLeft={() => dismissSuggestion(key, track.title)} onRight={adding ? undefined : () => enqueueWithUndo(track)}>{row}</SwipeSurface>}
                status={track.library_status ?? undefined} busy={preparing || adding || playback.busy[key]}
                onAction={(action) => { if (action === 'play') play(key); else if (action === 'queue') enqueueWithUndo(track); else void playback.act(track, action) }}
                onAddToPlaylist={() => setTarget({ title: track.title, provider: track.provider, externalId: track.external_id, trackId: track.track_id })}
                onDismissSuggestion={() => dismissSuggestion(key, track.title)}
                error={error ? { message: error.message, retry: () => void playback.act(track, error.action) } : undefined}
                onDismissError={() => playback.dismiss(`${track.provider}:${track.external_id}`)} />
            })}
          </ul>
          {loading && !suggestions.length ? <div className="flex items-center gap-2 px-2 py-3 text-sm text-text-muted"><Spinner size="sm" /> Finding similar songs…</div> : null}
          {message ? (
            <div className="px-2 py-2">
              <p role="status" className="text-xs text-text-muted">{message}</p>
              <Button variant="ghost" size="sm" onClick={retry}>Try again</Button>
            </div>
          ) : null}
        </>
      ) : null}
      {target ? <AddToPlaylistDialog target={target} onOpenChange={(open) => { if (!open) setTarget(null) }} /> : null}
    </section>
  )
}
